import React, { useState, useEffect, useContext, createContext } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  X,
  LayoutDashboard,
  Users,
  Mail,
  CreditCard,
  TrendingUp,
  Landmark,
  Percent,
  Settings,
  Globe,
  LogOut,
  ChevronRight,
  Bell,
  Package,
  Newspaper,
  Coins,
  Scale,
} from 'lucide-react';
import { useStore } from '../../hooks/useStore';
import { can, ROUTE_PERMISSIONS } from '../../utils/permissions';
import { toast } from 'sonner';

/**
 * Shell della dashboard admin — stile "dark sidebar" (rifacimento grafico).
 *
 * La navigazione mantiene gli stessi `href` delle rotte esistenti (App.tsx):
 * cambiano SOLO le etichette, semplificate e in italiano.
 *
 * Topbar context-aware: ogni pagina può iniettare le proprie azioni
 * (primaria "Crea…", secondaria "Esporta") via il hook `useTopbarActions`.
 * La topbar mostra: [Esporta] · [campanella notifiche] · [Crea], come nel mockup.
 */

// ── Topbar actions context ──────────────────────────────────────────────────
export interface TopbarAction {
  label: string;
  onClick: () => void;
  icon?: React.ComponentType<{ className?: string }>;
}
export interface TopbarConfig {
  primary?: TopbarAction; // bottone indaco (es. "Crea utente")
  secondary?: TopbarAction; // bottone outline (es. "Esporta")
}
const TopbarContext = createContext<{ setConfig: (c: TopbarConfig) => void }>({ setConfig: () => {} });

/**
 * Hook per le pagine: registra le azioni della topbar finché la pagina è montata
 * (o finché cambiano le `deps`). Le pulisce automaticamente allo smontaggio.
 */
export function useTopbarActions(config: TopbarConfig, deps: React.DependencyList) {
  const { setConfig } = useContext(TopbarContext);
  useEffect(() => {
    setConfig(config);
    return () => setConfig({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** Compatta i numeri grandi: 2700 → "2.7k", 1000 → "1k", 7 → "7". */
function formatCount(n?: number): string | undefined {
  if (n == null) return undefined;
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(n);
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const NAV_MAIN: NavItem[] = [
  { name: 'Panoramica', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Utenti', href: '/users', icon: Users },
  { name: 'Newsletter', href: '/newsletter', icon: Mail },
  { name: 'Blog', href: '/blog', icon: Newspaper },
  { name: 'Abbonamenti', href: '/subscriptions', icon: CreditCard },
  { name: 'Statistiche', href: '/subscription-stats', icon: TrendingUp },
  { name: 'Pagamenti', href: '/stripe', icon: Landmark },
  { name: 'Commissioni', href: '/commissions', icon: Percent },
  { name: 'Coin', href: '/coins', icon: Coins },
  { name: 'Pacchetti', href: '/packages', icon: Package },
];

const NAV_SYSTEM: NavItem[] = [
  { name: 'Configurazione', href: '/config', icon: Settings },
  { name: 'Documenti legali', href: '/legal', icon: Scale },
  { name: 'Lingue', href: '/languages', icon: Globe },
];

const ALL_NAV = [...NAV_MAIN, ...NAV_SYSTEM];

const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [topbarConfig, setTopbarConfig] = useState<TopbarConfig>({});
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, stats, loadDashboardStats } = useStore();

  // Carica lo snapshot stats (per il badge "Utenti" in sidebar) se non già in cache.
  useEffect(() => {
    if (!stats) {
      loadDashboardStats().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Match attivo: esatto o prefisso (es. /newsletter/new evidenzia "Newsletter").
  const isCurrent = (href: string) =>
    location.pathname === href || location.pathname.startsWith(href + '/');

  const current = ALL_NAV.find((n) => isCurrent(n.href));
  const pageTitle = current?.name ?? 'Panoramica';

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Disconnesso con successo');
      navigate('/login');
    } catch {
      toast.error('Errore durante la disconnessione');
    }
  };

  const initials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'A';
  const roleLabel = user?.userRole?.roleName === 'ADMIN' ? 'Amministratore' : (user?.userRole?.roleName ?? '');

  // RBAC: in sidebar solo le voci coperte dai permessi dell'utente.
  const allowed = (href: string) => can(user, ...(ROUTE_PERMISSIONS[href] ?? []));

  const sidebar = (
    <SidebarContent
      navMain={NAV_MAIN.filter((i) => allowed(i.href))}
      navSystem={NAV_SYSTEM.filter((i) => allowed(i.href))}
      isCurrent={isCurrent}
      onNavigate={(href) => {
        navigate(href);
        setSidebarOpen(false);
      }}
      onLogout={handleLogout}
      userName={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'Admin'}
      roleLabel={roleLabel}
      initials={initials}
      badges={{ '/users': formatCount(stats?.totalUsers) }}
    />
  );

  const PrimaryIcon = topbarConfig.primary?.icon;
  const SecondaryIcon = topbarConfig.secondary?.icon;

  return (
    <TopbarContext.Provider value={{ setConfig: setTopbarConfig }}>
      <div className="flex h-screen overflow-hidden bg-[#f4f5f7] text-slate-800">
        {/* Sidebar mobile (drawer) */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 flex md:hidden">
            <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
            <div className="relative w-[248px] h-full">
              {sidebar}
              <button
                className="absolute top-3 -right-11 h-9 w-9 grid place-items-center rounded-lg bg-white/10 text-white"
                onClick={() => setSidebarOpen(false)}
                aria-label="Chiudi menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Sidebar desktop */}
        <div className="hidden md:flex md:shrink-0">{sidebar}</div>

        {/* Contenuto */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <header className="h-[60px] shrink-0 bg-white border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6">
            <button
              className="md:hidden h-9 w-9 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-600"
              onClick={() => setSidebarOpen(true)}
              aria-label="Apri menu"
            >
              <Menu className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-2 text-sm text-slate-400 min-w-0">
              <span className="hidden sm:inline">Locally</span>
              <ChevronRight className="hidden sm:inline h-3.5 w-3.5 shrink-0" />
              <span className="text-slate-800 font-semibold truncate">{pageTitle}</span>
            </div>

            {/* Azioni topbar (come da mockup): Esporta · Notifiche · Crea */}
            <div className="ml-auto flex items-center gap-2">
              {topbarConfig.secondary && (
                <button
                  onClick={topbarConfig.secondary.onClick}
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
                >
                  {SecondaryIcon && <SecondaryIcon className="h-4 w-4" />}
                  <span className="hidden sm:inline">{topbarConfig.secondary.label}</span>
                </button>
              )}

              <div className="relative">
                <button
                  onClick={() => setNotifOpen((o) => !o)}
                  className="relative h-9 w-9 grid place-items-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors"
                  aria-label="Notifiche"
                >
                  <Bell className="h-[18px] w-[18px]" />
                  <span className="absolute top-1.5 right-2 h-1.5 w-1.5 rounded-full bg-indigo-500" />
                </button>
                {notifOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setNotifOpen(false)} />
                    <div className="absolute right-0 mt-2 w-72 rounded-xl border border-slate-200 bg-white shadow-lg z-20 overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">
                        Notifiche
                      </div>
                      <div className="px-4 py-8 text-center text-sm text-slate-400">
                        Nessuna notifica al momento.
                      </div>
                    </div>
                  </>
                )}
              </div>

              {topbarConfig.primary && (
                <button
                  onClick={topbarConfig.primary.onClick}
                  className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors"
                >
                  {PrimaryIcon && <PrimaryIcon className="h-4 w-4" />}
                  <span className="hidden sm:inline">{topbarConfig.primary.label}</span>
                </button>
              )}
            </div>
          </header>

          <main className="flex-1 overflow-y-auto p-4 sm:p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </TopbarContext.Provider>
  );
};

interface SidebarProps {
  isCurrent: (href: string) => boolean;
  onNavigate: (href: string) => void;
  onLogout: () => void;
  userName: string;
  roleLabel: string;
  initials: string;
  badges?: Record<string, string | undefined>;
  /** Voci già filtrate per permesso dal layout. */
  navMain: NavItem[];
  navSystem: NavItem[];
}

const SidebarContent: React.FC<SidebarProps> = ({
  isCurrent,
  onNavigate,
  onLogout,
  userName,
  roleLabel,
  initials,
  badges,
  navMain,
  navSystem,
}) => {
  const renderItem = (item: NavItem) => {
    const Icon = item.icon;
    const active = isCurrent(item.href);
    const badge = badges?.[item.href];
    return (
      <button
        key={item.href}
        onClick={() => onNavigate(item.href)}
        className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          active ? 'bg-indigo-500/15 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'
        }`}
      >
        {active && (
          <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-indigo-500" />
        )}
        <Icon className={`h-[18px] w-[18px] ${active ? 'text-indigo-300' : ''}`} />
        {item.name}
        {badge != null && (
          <span className="ml-auto text-[11px] font-semibold rounded-full px-2 py-0.5 min-w-[22px] text-center bg-white/10 text-slate-300">
            {badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flex flex-col w-[248px] h-full bg-[#0f1426] text-slate-200">
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 h-[60px] shrink-0">
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 grid place-items-center text-white font-extrabold shadow-lg shadow-indigo-500/30">
          L
        </div>
        <div>
          <p className="font-bold text-white leading-tight">Locally</p>
          <p className="text-[11px] text-slate-400 -mt-0.5">Admin</p>
        </div>
      </div>

      {/* Navigazione (già filtrata per permesso) */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navMain.map(renderItem)}
        {navSystem.length > 0 && <div className="pt-3 mt-3 border-t border-white/5" />}
        {navSystem.map(renderItem)}
      </nav>

      {/* Utente + logout */}
      <div className="m-3 rounded-xl bg-white/5 border border-white/5 p-3 shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 grid place-items-center text-white text-xs font-semibold">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{userName}</p>
            <p className="text-[11px] text-slate-400 truncate">{roleLabel}</p>
          </div>
          <button
            onClick={onLogout}
            className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:bg-white/10 hover:text-white"
            aria-label="Esci"
            title="Esci"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
